import React from 'react';
import { message } from 'antd';
import { connect } from 'react-redux';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	updateContractWorkOrder,
	updateInternalWorkspaceAttachmentsForSupplier,
} from '../../thunks/work_orders_thunks';
import {
	getContractorOrWorkOrderId,
	getContractorWO,
	getInternalNotesChannelId,
} from '../../utils/WorkOrderUtils';
import FileUploader from '../file_uploader/FileUploader';

interface InternalWorkspaceAttachmentsTabProps {
	workOrder: any;
	isClientView: boolean;
	internalMessages: any;
	updateInternalWorkspaceAttachments: (payload: { id: string; attachments: any[] }) => Promise<any>;
	updateSubContractorInfo: (entity: any) => Promise<any>;
}

const InternalWorkspaceAttachmentsTab = ({
	workOrder,
	isClientView,
	internalMessages,
	updateInternalWorkspaceAttachments,
	updateSubContractorInfo,
}: InternalWorkspaceAttachmentsTabProps) => {
	const contractorWO = getContractorWO(workOrder, isClientView);

	// Read from the same redux slice as InternalNotesPreviewCard
	// (`state.messages[channel].internalAttachments`) so the drawer reflects
	// attachments hydrated by fetchInternalWorkspace, not just whatever the
	// /work_order/:id response happened to include. The PATCH `attachments/update`
	// thunk syncs both slices, so uploads still appear immediately.
	const attachments = contractorWO
		? nullSafeGetOrElse('subInternalAttachments', contractorWO, [])
		: nullSafeGetOrElse('internalAttachments', internalMessages, []);

	const saveAttachments = (newAttachments) => {
		if (contractorWO) {
			return updateSubContractorInfo({
				id: contractorWO.id,
				subInternalAttachments: newAttachments,
			});
		}

		return updateInternalWorkspaceAttachments({
			id: workOrder.id,
			attachments: newAttachments,
		});
	};

	const handleAttachmentUpdateFailure = () => {
		message.error('Failed to upload document');
	};

	const handleAttachmentUpload = (newAttachments) => {
		const callUpdate = newAttachments.every(
			(element) => element && element.fileId && element.fileName
		);
		if (!callUpdate) return;

		const savePromise = saveAttachments(newAttachments);
		if (savePromise && typeof savePromise.catch === 'function') {
			savePromise.catch(handleAttachmentUpdateFailure);
		}
	};

	return (
		<div className="flex flex-col">
			{/* File Uploader */}
			<div className="mb-4">
				<FileUploader
					defaultFileList={attachments}
					roleType="supplier"
					handleUploadSuccess={handleAttachmentUpload}
					uploaderType={'dragger'}
					multiple
				/>
			</div>
		</div>
	);
};

const mapStateToProps = (state) => {
	const workOrder = state.work_orders.detail;
	const woId = getContractorOrWorkOrderId(workOrder) || nullSafeGet('id', workOrder);
	const channelId = woId ? getInternalNotesChannelId(woId) : null;
	return {
		workOrder,
		isClientView: state.clientView.isClientView,
		internalMessages: channelId ? state.messages[channelId] : null,
	};
};

const mapDispatchToProps = (dispatch) => ({
	updateInternalWorkspaceAttachments: (payload) =>
		dispatch(updateInternalWorkspaceAttachmentsForSupplier(payload)),
	updateSubContractorInfo: (entity) => dispatch(updateContractWorkOrder(entity, 'id')),
});

export default connect(mapStateToProps, mapDispatchToProps)(InternalWorkspaceAttachmentsTab);
